import { useEffect, useState } from 'react';
import { api } from '../../services/api';
import type { PackageHistoryAction, PackageHistoryEntry } from '../../types/packages';
import { actorName } from '../offers/OfferActivityCard';
import { formatDubaiInstant } from '../offers/offerEditModel';
import { HISTORY_ACTION_BADGE, HISTORY_ACTION_LABELS } from './packageReviewModel';

export interface PackageActivityCardProps {
  packageId: string;
  token: string;
  /** Changes after a decision or an edit, so the card reloads. */
  refreshKey?: number;
}

const shown = (v: string | null | undefined) => (v == null || v === '' ? '—' : v);

/** Spec 081b — the package's admin action log (`GET …/packages/{id}/history`), newest first. */
export default function PackageActivityCard(props: PackageActivityCardProps) {
  const { packageId, token, refreshKey } = props;
  const [entries, setEntries] = useState<PackageHistoryEntry[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(false);
    api.getPackageHistory(packageId, token)
      .then((rows) => { if (!cancelled) setEntries(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [packageId, token, refreshKey]);

  return (
    <div className="admin-card" data-testid="package-activity">
      <div className="admin-card-header">
        <h3 className="admin-card-title">Activity</h3>
      </div>
      {error && <div className="admin-info-banner danger" role="alert">Could not load the activity.</div>}
      {!error && entries == null && <div className="admin-text-muted" role="status">Loading activity…</div>}
      {entries?.length === 0 && <div className="admin-text-muted">No admin actions yet.</div>}
      {entries && entries.length > 0 && (
        <ol className="offer-activity">
          {entries.map((e) => {
            const action = e.action as PackageHistoryAction;
            return (
              <li key={e.id} className="offer-activity__item" data-testid="package-activity-item">
                <div className="offer-activity__head">
                  <span className={`admin-badge ${HISTORY_ACTION_BADGE[action] ?? 'neutral'}`}>{HISTORY_ACTION_LABELS[action] ?? e.action}</span>
                  <span className="offer-activity__who">{actorName({ adminSubjectId: e.adminSubjectId, adminName: e.adminName })}</span>
                  <time className="offer-activity__when" dateTime={e.occurredAt}>{formatDubaiInstant(e.occurredAt)}</time>
                </div>
                {e.reason && action !== 'PACKAGE_ADMIN_EDITED' && <div className="offer-activity__reason">{e.reason}</div>}
                {e.changes && e.changes.length > 0 && (
                  <ul className="offer-activity__changes">
                    {e.changes.map((c) => (
                      <li key={c.field}>
                        <span className="offer-activity__label">{c.label}:</span>{' '}
                        <s className="offer-activity__before">{shown(c.before)}</s>{' → '}
                        <strong className="offer-activity__after">{shown(c.after)}</strong>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
